import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";
import type { QaDisposition, ReleaseDetail, ReleaseScopeItem } from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Select, Textarea } from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";

const DISPOSITIONS: [QaDisposition, string][] = [
  ["REQUIRED", "Needs testing"],
  ["NOT_REQUIRED", "Does not need testing"],
  ["WAIVED", "Testing waived"],
];

/**
 * Whether an item in the release needs testing, and why (§24).
 *
 * <p>The reason is not optional decoration. Marking work as not needing QA is how a release
 * passes its QA gate without anybody testing that work, so the answer to "why did nobody test
 * this?" has to exist at the moment the decision is taken rather than be reconstructed afterwards.
 */
export function QaDispositionDrawer({
  release,
  item,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  item: ReleaseScopeItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [disposition, setDisposition] = useState<QaDisposition>("REQUIRED");
  const [reason, setReason] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  useEffect(() => {
    if (!item) {
      return;
    }
    setDisposition(item.qaRequired ?? "REQUIRED");
    setReason(item.qaDispositionReason ?? "");
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  // Excusing work from testing needs a reason; requiring it is the default and explains itself.
  const needsReason = disposition !== "REQUIRED";
  const canSubmit = !needsReason || reason.trim() !== "";

  const submit = () =>
    void run(
      () =>
        api.put(`/api/v1/releases/${release.id}/scope/items/${item?.id}/qa-disposition`, {
          disposition,
          reason: reason.trim() === "" ? null : reason.trim(),
        }),
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open={item != null}
      onClose={onClose}
      title="Does this need testing?"
      subtitle={item ? `${item.itemKey} — ${item.title}` : undefined}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit} loading={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        <Field label="Testing" required>
          <Select
            value={disposition}
            onChange={(event) => setDisposition(event.target.value as QaDisposition)}
          >
            {DISPOSITIONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>

        {needsReason && (
          <>
            <InlineAlert tone="warning">
              The QA gate will stop counting this item. Whoever reads this release afterwards sees
              your reason where the test result would have been.
            </InlineAlert>
            <Field label="Why does this not need testing?" required>
              <Textarea
                rows={3}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Configuration-only change, verified by the deployment check."
              />
            </Field>
          </>
        )}
      </div>
    </Drawer>
  );
}
